import { Box, Typography } from '@mui/material';
import { FC, Fragment, ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Spinner } from 'src/components/spinner';
import { getAmend } from 'src/utils/local-storage-versioning';
import { getTheme } from 'src/utils/getTheme';
import { useIsUserAuthenticated } from 'src/lib/hooks/use-is-user-authenticated';
import { usePostAmend } from 'src/lib/hooks/use-post-amend';
import { ProcessAmendDto } from 'src/api/amendService/dto/ProcessAmend.dto';

import { BookingUpdatedPage } from '../booking-updated';
import { BookingUpdateFailurePage } from '../booking-update-failure';
import { AuthContext } from '../../providers/auth-context';
import { getAmendSelfServeStorage } from '../../utils/local-storage-versioning';

import { styles } from './styles';

const { isHosTheme } = getTheme();

const TextWrapper: FC<{ children: ReactNode }> = (props) => {
    const Wrapper = isHosTheme ? Box : Fragment;

    return <Wrapper>{props.children}</Wrapper>;
};

export const ProcessingAmendPage: FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const amend = getAmend();
    const userState = useContext(AuthContext);
    const selfServe = getAmendSelfServeStorage();

    const { error: authenticationError, isFetching: isAuthenticatingUser } = useIsUserAuthenticated(
        !userState.loggedIn
    );
    const {
        error: amendError,
        isPending: isAmending,
        mutate: amendBooking,
        isSuccess: hasAmended,
    } = usePostAmend();

    const transactionId = searchParams.get('id');

    if (!transactionId || !amend.vitaRef || authenticationError || amendError)
        return <BookingUpdateFailurePage />;

    if (hasAmended) return <BookingUpdatedPage />;

    if (!isAuthenticatingUser && !isAmending) {
        const payload = new ProcessAmendDto(
            amend.vitaRef,
            transactionId,
            amend.roommates,
            selfServe
        );
        amendBooking(payload);
    }

    return (
        <Box css={styles.container}>
            <Box css={styles.imageContainer} />

            <Box css={styles.wrapper}>
                <TextWrapper>
                    <Typography variant="h1" css={styles.title}>
                        {t('booking_processing')}
                    </Typography>

                    <Typography variant="body3">{t('processing_info')}</Typography>
                </TextWrapper>

                <Spinner />
            </Box>
        </Box>
    );
};
